import { useEffect } from 'react'
import { View } from 'react-native'
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { colors } from '@ui/colors'
import Frame from './assets/frame'
import Person from './assets/person'
import {
  buildPhaseLoop,
  AnimationComponentProps as Props,
} from './phaseAnimation'

const REFERENCE_SIZE = 24

const FRAME_SIZE = [24, 24, 24, 24, 24]
const FRAME_OPACITY = [0.25, 0.25, 0.25, 1, 0.25]

const PERSON_SIZE = [12, 12, 12, 12, 12]
const PERSON_TRANSLATE_X = [0, -12, 12, 0, 0]
const PERSON_TRANSLATE_Y = [0, 0, 0, 0, 0]
const PERSON_OPACITY = [0, 1, 1, 1, 0]

const PHASE_DURATIONS = [300, 300, 400, 600, 200]
const PHASE_DELAYS = [0, 200, 200, 200, 800]
const PHASE_EASINGS = [
  Easing.out(Easing.ease),
  Easing.out(Easing.ease),
  Easing.out(Easing.ease),
  Easing.out(Easing.ease),
  Easing.linear,
]

export default function PersonFramePosition({
  size = REFERENCE_SIZE,
  color = colors.white,
  durations = PHASE_DURATIONS,
  delays = PHASE_DELAYS,
  easings = PHASE_EASINGS,
  isActive = true,
}: Props) {
  const scale = size / REFERENCE_SIZE

  const frameSize = useSharedValue(FRAME_SIZE[0] * scale)
  const frameOpacity = useSharedValue(FRAME_OPACITY[0])
  const personSize = useSharedValue(PERSON_SIZE[0] * scale)
  const personTranslateX = useSharedValue(PERSON_TRANSLATE_X[0] * scale)
  const personTranslateY = useSharedValue(PERSON_TRANSLATE_Y[0] * scale)
  const personOpacity = useSharedValue(0)

  useEffect(() => {
    if (!isActive) {
      cancelAnimation(frameSize)
      cancelAnimation(frameOpacity)
      cancelAnimation(personSize)
      cancelAnimation(personTranslateX)
      cancelAnimation(personTranslateY)
      cancelAnimation(personOpacity)
      return
    }

    frameSize.set(
      buildPhaseLoop(
        FRAME_SIZE.map((value) => value * scale),
        durations,
        delays,
        easings
      )
    )

    frameOpacity.set(buildPhaseLoop(FRAME_OPACITY, durations, delays, easings))

    personSize.set(
      buildPhaseLoop(
        PERSON_SIZE.map((value) => value * scale),
        durations,
        delays,
        easings
      )
    )

    personTranslateX.set(
      buildPhaseLoop(
        PERSON_TRANSLATE_X.map((value) => value * scale),
        durations,
        delays,
        easings
      )
    )

    personTranslateY.set(
      buildPhaseLoop(
        PERSON_TRANSLATE_Y.map((value) => value * scale),
        durations,
        delays,
        easings
      )
    )

    personOpacity.set(
      buildPhaseLoop(PERSON_OPACITY, durations, delays, easings)
    )
  }, [
    isActive,
    scale,
    durations,
    delays,
    easings,
    frameSize,
    frameOpacity,
    personSize,
    personTranslateX,
    personTranslateY,
    personOpacity,
  ])

  const frameStyle = useAnimatedStyle(() => ({
    width: frameSize.value,
    height: frameSize.value,
    opacity: frameOpacity.value,
  }))

  const personStyle = useAnimatedStyle(() => ({
    width: personSize.value,
    height: personSize.value,
    opacity: personOpacity.value,
    transform: [
      { translateX: personTranslateX.value },
      { translateY: personTranslateY.value },
    ],
  }))

  return (
    <View
      style={{ width: size, height: size }}
      className="items-center justify-center"
    >
      <Animated.View className="absolute" style={frameStyle}>
        <Frame size="100%" color={color} strokeWidth={2} />
      </Animated.View>
      <Animated.View className="absolute" style={personStyle}>
        <Person size="100%" color={color} />
      </Animated.View>
    </View>
  )
}
